<script setup>
import {ref, computed} from 'vue'

const props = defineProps({
    src: {
        type: String,
        required: true,
    },
    // file name without the extension; no name, no download button
    downloadName: {
        type: String,
        default: '',
    },
})

const audioRef = ref(null)
const isPlaying = ref(false)
const isDownloading = ref(false)
const duration = ref(0)
const currentTime = ref(0)

const progressPercent = computed(() => {
    if (!duration.value) return 0
    return (currentTime.value / duration.value) * 100
})

const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const total = Math.floor(seconds)
    const mins = Math.floor(total / 60)
    const secs = total % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
}

const togglePlay = () => {
    const audio = audioRef.value
    if (!audio) return

    if (audio.paused) {
        audio.play()
        isPlaying.value = true
    } else {
        audio.pause()
        isPlaying.value = false
    }
}

const onTimeUpdate = () => {
    if (!audioRef.value) return
    currentTime.value = audioRef.value.currentTime
}

const onLoadedMetadata = () => {
    if (!audioRef.value) return
    duration.value = audioRef.value.duration
}

const onEnded = () => {
    isPlaying.value = false
    currentTime.value = 0
}

// the recordings storage sends no CORS headers and no Content-Disposition:
// the blob route fails there and a new tab with the browser's player is the
// fallback; once either header is set the file downloads under our name
const download = async () => {
    isDownloading.value = true

    try {
        const response = await fetch(props.src)
        if (!response.ok) throw new Error(response.status)

        const extension = new URL(props.src).pathname.split('.').pop()
        const url = URL.createObjectURL(await response.blob())
        const link = document.createElement('a')
        link.href = url
        link.download = `${props.downloadName}.${extension}`
        link.click()
        URL.revokeObjectURL(url)
    } catch (error) {
        window.open(props.src, '_blank', 'noopener')
    } finally {
        isDownloading.value = false
    }
}

const seek = (event) => {
    if (!audioRef.value || !duration.value) return

    const bar = event.currentTarget
    const rect = bar.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const ratio = Math.min(Math.max(clickX / rect.width, 0), 1)

    audioRef.value.currentTime = ratio * duration.value
}
</script>

<template>
    <div class="audio-card">
        <button class="play-btn" type="button" @click="togglePlay">
            <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"
                 fill="none">
                <path
                    d="M11.668 6.26796C13.0013 7.03776 13.0013 8.96226 11.668 9.73206L7.66797 12.0415C6.33464 12.8113 4.66797 11.849 4.66797 10.3094L4.66797 5.69061C4.66797 4.15101 6.33463 3.18876 7.66797 3.95856L11.668 6.26796Z"
                    fill="#7A70DF"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                    d="M3.33203 5.00001C3.33203 4.07953 4.07822 3.33334 4.9987 3.33334C5.91917 3.33334 6.66536 4.07954 6.66536 5.00001V11C6.66536 11.9205 5.91917 12.6667 4.9987 12.6667C4.07822 12.6667 3.33203 11.9205 3.33203 11V5.00001Z"
                    fill="#7A70DF"/>
                <path
                    d="M9.33203 5.00001C9.33203 4.07953 10.0782 3.33334 10.9987 3.33334C11.9192 3.33334 12.6654 4.07954 12.6654 5.00001V11C12.6654 11.9205 11.9192 12.6667 10.9987 12.6667C10.0782 12.6667 9.33203 11.9205 9.33203 11V5.00001Z"
                    fill="#7A70DF"/>
            </svg>
        </button>

        <div class="track">
            <div class="progress-wrapper" @click="seek">
                <div class="progress-bg"></div>
                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>

            <div class="time-row">
                <span class="time">{{ formatTime(currentTime) }}</span>
                <span class="time">{{ formatTime(duration || 144) }}</span>
            </div>
        </div>

        <button
            v-if="downloadName"
            class="download-btn"
            type="button"
            :disabled="isDownloading"
            :title="$t('telephony.calls_history.download')"
            :aria-label="$t('telephony.calls_history.download')"
            @click="download"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"
                 stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 2.5v7.5"/>
                <path d="M4.75 7L8 10.25 11.25 7"/>
                <path d="M2.75 11.5v1a1.25 1.25 0 0 0 1.25 1.25h8a1.25 1.25 0 0 0 1.25-1.25v-1"/>
            </svg>
        </button>

        <audio
            ref="audioRef"
            :src="props.src"
            @timeupdate="onTimeUpdate"
            @loadedmetadata="onLoadedMetadata"
            @ended="onEnded"
        ></audio>
    </div>
</template>

<style scoped>
.audio-card {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;

    padding: 9px;
    border-radius: 9px;
    background: #F4F3FC;
}

.play-btn {
    width: 16px;
    height: 16px;
    border: none;
    padding: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    background: transparent;
    cursor: pointer;
}

.track {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.progress-wrapper {
    position: relative;
    height: 4px;
    border-radius: 9999px;
    cursor: pointer;
    overflow: hidden;
}

.progress-bg {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.17);
}

.progress-fill {
    position: absolute;
    inset: 0;
    width: 0;
    background: #7A70DF;
}

.time-row {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    margin-top: 2px;
}

.time {
    color: var(--text-light-color);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}
.download-btn {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 6px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: transparent;
    color: var(--general-color);
    cursor: pointer;
    transition: background-color 0.2s;
}

.download-btn:hover {
    background: #7a70df1f;
}

.download-btn:disabled {
    cursor: progress;
    opacity: 0.5;
}
</style>
